/**
 * RegisterCTA — orange pill button that opens a registration URL in a new
 * tab. Hover/active colors and the press scale are pure CSS pseudo-classes
 * (`hover:`/`active:`), not JS mouse-state — no handlers means no need for
 * 'use client', which keeps this a server component wherever it's used.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { V3_SHADOWS } from '@/lib/design-tokens';

export interface RegisterCTAProps {
  href: string;
  /** Pill height in px. Defaults to 44. Radius derives from size: >=44 → 26, else 21. */
  size?: 40 | 44 | 46;
  fullWidth?: boolean;
  children?: ReactNode;
  className?: string;
}

export function RegisterCTA({
  href,
  size = 44,
  fullWidth = false,
  children = 'Register now',
  className,
}: RegisterCTAProps) {
  const radius = size >= 44 ? 26 : 21;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      className={cn(
        'items-center justify-center whitespace-nowrap px-[20px]',
        "font-['Work_Sans'] text-[14px] font-semibold text-white no-underline",
        // Press feel standardized to PRESSABLE (design-tokens.ts, matching
        // ExpandingCapsule): duration/scale merged with the existing
        // background-color transition into one transition-property list
        // (can't just append PRESSABLE — see its GOTCHA comment) with only
        // the scale value gated behind motion-safe.
        'bg-[#E36001] transition-[background-color,transform] duration-[160ms] ease-out',
        'hover:bg-[#f26a0c] motion-safe:active:scale-[0.96] active:bg-[#cf5600]',
        fullWidth ? 'flex w-full' : 'inline-flex',
        className
      )}
      style={{
        height: size,
        borderRadius: radius,
        boxShadow: V3_SHADOWS.cta,
      }}
    >
      {children}
    </a>
  );
}
